import { useRef, useState, type ChangeEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FileUp } from 'lucide-react';

import { IMPORT_CSV_HEADERS, importEmployees, type ImportEmployeesResult } from '@/api/invitations.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';

/**
 * CSV roster import — three-step flow inside one modal:
 *
 *   1. input   — paste CSV text or pick a .csv file
 *   2. preview — server-side dry run; per-row validation table
 *   3. done    — commit results summary
 *
 * The server is the single source of validation truth: the preview IS
 * the dry-run response, so what you see is exactly what a commit will
 * accept. Commit is disabled until every row validates.
 */

type Step = 'input' | 'preview' | 'done';

const ERROR_COPY: Record<string, string> = {
  missing_first_name: 'First name is missing',
  missing_last_name: 'Last name is missing',
  missing_email: 'Email is missing',
  invalid_email: 'Email is not a valid address',
  duplicate_email_in_file: 'Duplicate email within the file',
  unknown_department: 'Department slug does not exist in this org',
  invalid_role: 'Role must be employee, department_lead, quality_lead, or qa_personnel',
  unknown_manager: 'Manager email matches no employee or file row',
  manager_is_self: 'A person cannot be their own manager',
};

function describeRowError(code: string): string {
  return ERROR_COPY[code] ?? code;
}

export function ImportCsvModal({
  open,
  onClose,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  /** Called after a successful commit so the caller can invalidate queries. */
  onImported: () => void;
}) {
  const { show } = useToast();
  const [step, setStep] = useState<Step>('input');
  const [csv, setCsv] = useState('');
  const [sendInvites, setSendInvites] = useState(true);
  const [preview, setPreview] = useState<ImportEmployeesResult | null>(null);
  const [result, setResult] = useState<ImportEmployeesResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const dryRunMutation = useMutation({
    mutationFn: () => importEmployees({ csv, sendInvites, dryRun: true }),
    onSuccess: (data) => {
      setPreview(data);
      setStep('preview');
    },
  });

  const commitMutation = useMutation({
    mutationFn: () => importEmployees({ csv, sendInvites, dryRun: false }),
    onSuccess: (data) => {
      setResult(data);
      setStep('done');
      onImported();
      show(`Imported ${String(data.summary.created ?? 0)} new employee(s)`, { variant: 'success' });
    },
  });

  function reset(): void {
    setStep('input');
    setCsv('');
    setPreview(null);
    setResult(null);
    dryRunMutation.reset();
    commitMutation.reset();
  }

  function handleClose(): void {
    reset();
    onClose();
  }

  function handleFilePick(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (!file) return;
    void file.text().then((text) => {
      setCsv(text);
    });
    // Allow re-picking the same file later.
    e.target.value = '';
  }

  const validCount = preview?.summary.valid ?? 0;
  const invalidCount = preview?.summary.invalid ?? 0;

  return (
    <Modal open={open} onClose={handleClose} title="Import employees from CSV" size="lg">
      {step === 'input' ? (
        <div className="space-y-4">
          <p className="text-[13px] text-kw-muted">
            Paste CSV text or choose a file. Expected header row:{' '}
            <code className="rounded bg-kw-bg px-1 py-0.5 text-[11px] text-kw-shark">
              {IMPORT_CSV_HEADERS}
            </code>
            . Only <strong>firstName, lastName, email</strong> are required per row; departmentSlug, jobTitle,
            role, and managerEmail are optional.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
              <FileUp className="size-4" />
              Choose CSV file
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              aria-label="CSV file"
              onChange={handleFilePick}
            />
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-medium text-kw-muted">CSV content</span>
            <textarea
              value={csv}
              onChange={(e) => {
                setCsv(e.target.value);
              }}
              rows={8}
              placeholder={`${IMPORT_CSV_HEADERS}\nAda,Lovelace,ada@example.com,engineering,Engineer,employee,`}
              className="w-full rounded-kw-md border border-kw-border bg-white p-3 font-mono text-[12px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
            />
          </label>

          <label className="flex items-center gap-2 text-[13px] text-kw-shark">
            <input
              type="checkbox"
              checked={sendInvites}
              onChange={(e) => {
                setSendInvites(e.target.checked);
              }}
              className="size-4 accent-kw-cerulean"
            />
            Send portal invitations to imported people
          </label>

          {dryRunMutation.error ? <MutationErrorNote error={dryRunMutation.error} /> : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="button"
              isLoading={dryRunMutation.isPending}
              disabled={!csv.trim()}
              onClick={() => {
                dryRunMutation.mutate();
              }}
            >
              Preview import
            </Button>
          </div>
        </div>
      ) : step === 'preview' && preview ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-[13px]">
            <Badge variant="success">{validCount} ready</Badge>
            {invalidCount > 0 ? <Badge variant="danger">{invalidCount} with errors</Badge> : null}
            <span className="text-kw-muted">Nothing has been written yet — this is a dry-run preview.</span>
          </div>

          <div className="max-h-72 overflow-auto rounded-kw-md border border-kw-border">
            <table className="w-full text-left text-[12px]">
              <thead className="sticky top-0 bg-kw-bg text-kw-muted">
                <tr>
                  <th className="px-3 py-2 font-medium">Line</th>
                  <th className="px-3 py-2 font-medium">Email</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-kw-border">
                {preview.rows.map((row) => (
                  <tr key={row.line} className={row.ok ? '' : 'bg-kw-danger-bg/40'}>
                    <td className="px-3 py-2 text-kw-muted">{row.line}</td>
                    <td className="px-3 py-2 text-kw-shark">{row.email || '—'}</td>
                    <td className="px-3 py-2">
                      {row.ok ? (
                        <span className="inline-flex items-center gap-1 text-kw-tag-text">
                          <CheckCircle2 className="size-3.5" />
                          Ready
                        </span>
                      ) : (
                        <ul className="space-y-0.5 text-kw-danger-text">
                          {row.errors.map((err) => (
                            <li key={err}>{describeRowError(err)}</li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {invalidCount > 0 ? (
            <p className="text-[12px] text-kw-muted">
              Fix the rows above in your file, then go back and preview again. Import is disabled until every
              row validates.
            </p>
          ) : null}

          {commitMutation.error ? <MutationErrorNote error={commitMutation.error} /> : null}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setStep('input');
              }}
            >
              Back
            </Button>
            <Button
              type="button"
              isLoading={commitMutation.isPending}
              disabled={invalidCount > 0 || validCount === 0}
              onClick={() => {
                commitMutation.mutate();
              }}
            >
              Import {validCount} {validCount === 1 ? 'person' : 'people'}
              {sendInvites ? ' + send invites' : ''}
            </Button>
          </div>
        </div>
      ) : step === 'done' && result ? (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-kw-md border border-kw-tag-text/20 bg-kw-emerald-light p-3">
            <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-kw-tag-text" />
            <div className="text-[13px] text-kw-tag-text">
              <p className="font-medium">Import complete</p>
              <p className="mt-0.5">
                {String(result.summary.created ?? 0)} created, {String(result.summary.existing ?? 0)} already
                in the directory
                {sendInvites
                  ? `, ${String(result.summary.invited ?? 0)} invited, ${String(
                      result.summary.alreadyMembers ?? 0,
                    )} already members`
                  : ''}
                .
              </p>
            </div>
          </div>

          <div className="max-h-60 overflow-auto rounded-kw-md border border-kw-border">
            <table className="w-full text-left text-[12px]">
              <thead className="sticky top-0 bg-kw-bg text-kw-muted">
                <tr>
                  <th className="px-3 py-2 font-medium">Email</th>
                  <th className="px-3 py-2 font-medium">Directory</th>
                  <th className="px-3 py-2 font-medium">Invite</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-kw-border">
                {result.rows.map((row) => (
                  <tr key={row.line}>
                    <td className="px-3 py-2 text-kw-shark">{row.email}</td>
                    <td className="px-3 py-2">
                      <Badge variant={row.employee === 'created' ? 'success' : 'neutral'}>
                        {row.employee === 'created' ? 'Created' : 'Exists'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      {row.invite === 'invited' ? (
                        <Badge variant="info">Invited</Badge>
                      ) : row.invite === 'already_member' ? (
                        <Badge variant="neutral">Already a member</Badge>
                      ) : row.invite === 'already_invited' ? (
                        <Badge variant="neutral">Invite already pending</Badge>
                      ) : row.invite === 'failed' ? (
                        <Badge variant="danger">Invite failed</Badge>
                      ) : (
                        <span className="text-kw-ghost">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end">
            <Button type="button" onClick={handleClose}>
              Done
            </Button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function MutationErrorNote({ error }: { error: unknown }) {
  let message = error instanceof Error ? error.message : 'Import failed';
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { error?: unknown; message?: unknown } } }).response;
    if (typeof response?.data?.message === 'string') message = response.data.message;
    else if (response?.data?.error === 'csv_parse_failed') message = 'Could not parse the CSV file.';
    else if (response?.data?.error === 'missing_columns') message = 'The CSV is missing required columns.';
  }
  return (
    <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
      <p className="text-[12px] text-kw-danger-text">{message}</p>
    </div>
  );
}
